import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Row, Col, Card, Form, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axiosInstance from '../../services/AxiosInstance';
import { accessControlService } from '../../services/accessControlService';
import { getRoleBasedUserFilters } from '../../utils/roleAccess';
import InternalFooter from '../components/InternalFooter/InternalFooter';
import CommonTable from '../components/CommonTable/CommonTable';
import './commonstyle.css';
import '../tasks/tasklist.css';
import '../users/userlist.css';

const AccessControl = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [menuSections, setMenuSections] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [fullAccess, setFullAccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadingUser, setLoadingUser] = useState(false);

  const notify = (msg, isError = false) => {
    (isError ? toast.error : toast.success)(msg, {
      position: 'top-right',
      autoClose: 5000,
    });
  };

  const fetchUsers = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      const corporateId = userData?.corporateId || userData?.id;
      const filters = await getRoleBasedUserFilters(axiosInstance);
      const params = new URLSearchParams();
      params.append('corporate_id', corporateId);
      if (filters.branch) params.append('branch', filters.branch);
      if (filters.department) params.append('department', filters.department);
      if (filters.sub_department) params.append('sub_department', filters.sub_department);
      params.append('limit', 500);
      const res = await axiosInstance.get(`/list/corporateuserslist?${params}`);
      const list = res.data?.users || res.data?.data || [];
      setUsers(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Fetch users error:', err);
      notify('Failed to load users', true);
    }
  };

  const fetchMenuSections = async () => {
    try {
      const res = await accessControlService.getMenuSections();
      if (res?.data?.success && res?.data?.data) {
        setMenuSections(res.data.data);
      }
    } catch (err) {
      console.error('Fetch menu sections error:', err);
      notify('Failed to load menu sections', true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchMenuSections();
  }, []);

  useEffect(() => {
    const userId = location.state?.userId;
    if (userId && users.length > 0) {
      setSelectedUserId(String(userId));
      window.history.replaceState({}, document.title, location.pathname);
    }
  }, [location.state?.userId, users.length, location.pathname]);

  useEffect(() => {
    if (!selectedUserId) {
      setSelectedIds(new Set());
      setFullAccess(false);
      setLoadingUser(false);
      return;
    }
    setLoadingUser(true);
    accessControlService
      .getUserAccess(selectedUserId)
      .then((res) => {
        if (res?.data?.success && res?.data?.data) {
          const ids = res.data.data.menuSectionIds || [];
          setSelectedIds(new Set(ids));
          const allIds = collectAllSectionIds(menuSections);
          setFullAccess(ids.length > 0 && allIds.length > 0 && ids.length >= allIds.length);
        }
      })
      .catch((err) => {
        console.error('Fetch user access error:', err);
        notify('Failed to load user access', true);
      })
      .finally(() => setLoadingUser(false));
  }, [selectedUserId, menuSections.length]);

  const collectAllSectionIds = (sections) => {
    const ids = [];
    const walk = (items) => {
      if (!items) return;
      for (const item of items) {
        if (item.id) ids.push(item.id);
        if (item.children?.length) walk(item.children);
      }
    };
    walk(sections);
    return ids;
  };

  const flattenSectionsForTable = (sections) => {
    const rows = [];
    const walk = (items, parentName = '') => {
      if (!items) return;
      for (const item of items) {
        if (item.id) {
          rows.push({
            id: item.id,
            name: item.name,
            parentName: parentName || '—',
            routePath: item.route_path || '—',
          });
        }
        if (item.children?.length) walk(item.children, item.name);
      }
    };
    walk(sections);
    return rows;
  };

  const getDescendantIds = (sections, parentId) => {
    const ids = [];
    const findItem = (items, targetId) => {
      if (!items) return null;
      for (const item of items) {
        if (item.id === targetId) return item;
        const found = findItem(item.children, targetId);
        if (found) return found;
      }
      return null;
    };
    const collectDescendants = (items) => {
      if (!items) return;
      for (const item of items) {
        if (item.id) ids.push(item.id);
        if (item.children?.length) collectDescendants(item.children);
      }
    };
    const item = findItem(sections, parentId);
    if (item?.children?.length) collectDescendants(item.children);
    return ids;
  };

  const toggleSection = (id, checked) => {
    if (!checked) setFullAccess(false);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
        const descendantIds = getDescendantIds(menuSections, id);
        descendantIds.forEach((childId) => next.add(childId));
      } else {
        next.delete(id);
        const descendantIds = getDescendantIds(menuSections, id);
        descendantIds.forEach((childId) => next.delete(childId));
      }
      return next;
    });
  };

  const handleFullAccessChange = (checked) => {
    setFullAccess(checked);
    if (checked) {
      const allIds = collectAllSectionIds(menuSections);
      setSelectedIds(new Set(allIds));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSave = async () => {
    if (!selectedUserId) {
      notify('Please select a user', true);
      return;
    }
    setSaving(true);
    try {
      await accessControlService.saveUserAccess(selectedUserId, {
        fullAccess: fullAccess || undefined,
        menuSectionIds: fullAccess ? undefined : Array.from(selectedIds),
      });
      notify('Access saved successfully');
      window.dispatchEvent(new Event('menu-access-updated'));
      if (location.state?.fromUserList) {
        navigate('/settings/userlist');
      }
    } catch (err) {
      console.error('Save access error:', err);
      notify(err?.response?.data?.error || 'Failed to save access', true);
    } finally {
      setSaving(false);
    }
  };


  const tableData = flattenSectionsForTable(menuSections);
  const columns = [
    {
      key: 'name',
      header: 'Section',
      render: (row) =>
        row.parentName && row.parentName !== '—' ? (
          <span style={{ paddingLeft: '1.5rem' }}>{row.name}</span>
        ) : (
          row.name
        ),
    },
    {
      key: 'access',
      header: 'Access',
      render: (row) => (
        <Form.Check
          type="checkbox"
          id={`section-${row.id}`}
          checked={selectedIds.has(row.id)}
          onChange={(e) => toggleSection(row.id, e.target.checked)}
          onClick={(e) => e.stopPropagation()}
        />
      ),
    },
  ];

  return (
    <>
      <Row style={{ marginBottom: '0px !important' }}>
        <ToastContainer />
        <Col lg={12}>
          <Card className="user-list-card access-control-card">
            <Card.Body className="access-control-card-body">
              <div className="filter-group mb-4" style={{ maxWidth: '400px' }}>
                <label>Select User</label>
                <Select
                  options={users.map((u) => {
                    const id = u.CorporateUserId || u.id;
                    const name = u.name || u.full_name || `User ${id}`;
                    const email = u.email || u.User?.email || '';
                    return {
                      value: String(id),
                      label: email ? `${name} (${email})` : name,
                    };
                  })}
                  value={selectedUserId
                    ? (() => {
                        const u = users.find((u) => String(u.CorporateUserId || u.id) === selectedUserId);
                        const name = u?.name || u?.full_name || `User ${selectedUserId}`;
                        const email = u?.email || u?.User?.email || '';
                        return { value: selectedUserId, label: email ? `${name} (${email})` : name };
                      })()
                    : null}
                  onChange={(opt) => setSelectedUserId(opt?.value || '')}
                  isSearchable
                  isClearable
                  placeholder="-- Select User --"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={{
                    control: (base) => ({
                      ...base,
                      border: '1px solid #e9ecef',
                      borderRadius: '6px',
                      minHeight: '38px',
                    }),
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                />
              </div>

              {selectedUserId && (
                <>
                  <div className="mb-3 access-control-full-access-row">
                    <Form.Check
                      type="checkbox"
                      id="fullAccess"
                      label="Full access to all sections"
                      checked={fullAccess}
                      onChange={(e) => handleFullAccessChange(e.target.checked)}
                      className="access-control-full-access-check"
                    />
                  </div>

                  <hr />

                  <Form.Label className="mb-2 access-control-section-label">Feature Accesses (Please save before leaving)</Form.Label>
                  {loading || loadingUser ? (
                    <div className="loading-container">
                      <div className="loading-spinner"></div>
                    </div>
                  ) : (
                    <div className="row">
                      <CommonTable
                        columns={columns}
                        data={tableData}
                        emptyMessage="No feature accesses found"
                        emptyStateIcon="fas fa-list"
                        getRowId={(row) => row.id}
                        maxHeight="70vh"
                        minHeight="400px"
                      />
                    </div>
                  )}

                  <div className="mt-4 d-flex justify-content-end">
                    <button
                      type="button"
                      className="new-user-btn"
                      onClick={handleSave}
                      disabled={saving || loadingUser || (selectedIds.size === 0 && !fullAccess)}
                      style={{ border: 'none', cursor: saving || loadingUser || (selectedIds.size === 0 && !fullAccess) ? 'not-allowed' : 'pointer' }}
                    >
                      {saving ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save me-2"></i>
                          Save
                        </>
                      )}
                    </button>
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <InternalFooter />
    </>
  );
};

export default AccessControl;
